import { useEffect, useState, useCallback, useRef } from 'react'
import { syncAllData, getSyncStatus, processSyncQueue, needsSync, type SyncStatus } from '../services/syncService'
import { initDB, getAll, STORES } from '../services/indexedDBService'
import { useAuthStore } from '../store/authStore'
import { toast } from '../utils/toast'

export function useOfflineSync() {
  const [status, setStatus] = useState<SyncStatus | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const syncInProgressRef = useRef(false)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  // Sincronização manual com useCallback para estabilizar a referência
  const sync = useCallback(async () => {
    if (syncInProgressRef.current || !navigator.onLine) {
      return
    }

    syncInProgressRef.current = true
    setIsSyncing(true)
    try {
      const newStatus = await syncAllData()
      setStatus(newStatus)
      return newStatus
    } catch (error) {
      console.error('❌ Erro na sincronização:', error)
      throw error
    } finally {
      setIsSyncing(false)
      syncInProgressRef.current = false
    }
  }, [])

  // Inicializa o IndexedDB
  useEffect(() => {
    initDB()
      .then(() => {
        console.log('✅ IndexedDB pronto')
        setIsInitialized(true)
        return getSyncStatus()
      })
      .then(setStatus)
      .catch((error: Error) => console.error('❌ Erro ao inicializar IndexedDB:', error))
  }, [])

  // Sincroniza automaticamente quando estiver online E autenticado
  useEffect(() => {
    if (!isInitialized || !isAuthenticated) return

    const handleOnline = async () => {
      console.log('🌐 Conexão restaurada - processando fila primeiro...')

      // Conta pedidos pendentes antes de processar para notificar o usuário
      const queue = await getAll(STORES.SYNC_QUEUE)
      const pedidosPendentes = queue.filter((action: any) => action.entity === 'pedido').length

      // IMPORTANTE: Processa fila ANTES de sincronizar dados do servidor
      const result = await processSyncQueue()

      if (result.success > 0) {
        if (pedidosPendentes > 0) {
          toast.success(`${pedidosPendentes} pedido(s) pendente(s) sincronizado(s) e enviado(s) para processamento`)
        } else {
          toast.success('Dados pendentes sincronizados com sucesso')
        }
      }

      console.log('🌐 Agora sincronizando dados do servidor...')
      await sync()
    }

    window.addEventListener('online', handleOnline)
    return () => window.removeEventListener('online', handleOnline)
  }, [isInitialized, isAuthenticated, sync])

  // Sincroniza na inicialização se necessário (apenas uma vez) E autenticado
  useEffect(() => {
    if (!isInitialized || !navigator.onLine || !isAuthenticated) return

    let mounted = true

    // Verifica se há fila pendente primeiro
    const processAndSync = async () => {
      const queue = await getAll(STORES.SYNC_QUEUE)
      
      if (queue.length > 0) {
        console.log(`📤 ${queue.length} ações pendentes - processando antes de sincronizar...`)
        await processSyncQueue()
      }

      const needs = await needsSync()
      if (needs && mounted && !syncInProgressRef.current) {
        console.log('📊 Dados desatualizados - sincronizando...')
        await sync()
      }
    }

    processAndSync()

    return () => {
      mounted = false
    }
  }, [isInitialized, isAuthenticated, sync])

  // Sincronização automática periódica a cada 1 hora (quando online e autenticado)
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !navigator.onLine) return

    console.log('⏰ Agendando sincronização automática a cada 1 hora')
    
    const ONE_HOUR = 3600000 // 1 hora em milissegundos
    const intervalId = setInterval(async () => {
      if (navigator.onLine && !syncInProgressRef.current) {
        console.log('⏰ Sincronização automática periódica iniciada')
        await sync()
      }
    }, ONE_HOUR)

    return () => {
      console.log('⏰ Cancelando sincronização automática periódica')
      clearInterval(intervalId)
    }
  }, [isInitialized, isAuthenticated, sync])

  // Atualiza status com useCallback
  const refreshStatus = useCallback(async () => {
    const newStatus = await getSyncStatus()
    setStatus(newStatus)
    return newStatus
  }, [])

  return {
    status,
    isSyncing,
    isInitialized,
    sync,
    refreshStatus,
  }
}
